/* eslint-disable @typescript-eslint/naming-convention */
// eslint-disable react/no-danger
/*
 * Helpers for Control Center Locale translations
 * Handled ?lang=fr_CA on the URL to set the locale
 */
import i18next from "i18next";
import DOMPurify from "dompurify";

let localeData = {};
let currentLocale = "en-US";

export class Locale {
  static setLabelsOnce(localeDataParm) {
    if (localeData !== localeDataParm) {
      localeData = localeDataParm;
      const resources = {};
      Object.entries(localeData).forEach(([key]) => {
        if (Object.prototype.hasOwnProperty.call(localeData, key)) {
          resources[key] = {
            translation: { ...localeData[key] } // Use default namespace
          };
        }
      });
      i18next.init({
        lng: "en-US",
        debug: false,
        resources,
        interpolation: { prefix: "{", suffix: "}" }
      });
      if (currentLocale !== "en-US") {
        Locale.change(currentLocale);
      }
    }
  }

  static exists(locale) {
    return locale in localeData;
  }

  static change(newLocale) {
    if (Locale.exists(newLocale)) {
      i18next.changeLanguage(newLocale);
      currentLocale = newLocale;
    }
    return currentLocale;
  }

  static getText(label: string, options = {}): string | React.ReactElement {
    const rval = i18next.t(label, options);
    if (rval) {
      const isBadActor =
        /<(basefont|input|iframe).*?>|<(applet|form|frameset|iframe|ruby|object|script).*?<\/\2>/i.test(
          rval
        );
      if (!isBadActor) {
        const isHTML =
          /<(br|basefont|hr|input|source|frame|param|area|meta|!--|col|link|option|base|img|wbr|!DOCTYPE).*?>|<(a|abbr|acronym|address|applet|article|aside|audio|b|bdi|bdo|big|blockquote|body|button|canvas|caption|center|cite|code|colgroup|command|datalist|dd|del|details|dfn|dialog|dir|div|dl|dt|em|embed|fieldset|figcaption|figure|font|footer|form|frameset|head|header|hgroup|h1|h2|h3|h4|h5|h6|html|i|iframe|ins|kbd|keygen|label|legend|li|map|mark|menu|meter|nav|noframes|noscript|object|ol|optgroup|output|p|pre|progress|q|rp|rt|ruby|s|samp|script|section|select|small|span|strike|strong|style|sub|sort-row|sup|table|tbody|td|textarea|tfoot|th|thead|time|title|tr|track|tt|u|ul|var|video).*?<\/\2>/i.test(
            rval
          );
        if (isHTML) {
          return Locale.sanitizedHtml(rval);
        }
      }
    }
    return rval;
  }

  static getCurrent() {
    return currentLocale;
  }

  static getPrice(value) {
    const val = typeof value !== "number" ? Number(value) : value;
    const locale = Locale.getCurrent();
    const currency =
      {
        "en-US": "USD",
        "en-CA": "CAD",
        "fr-CA": "CAD",
        "es-US": "USD"
      }[locale] || "USD";
    const formatter = new Intl.NumberFormat(Locale.getCurrent(), {
      style: "currency",
      currency,
      currencyDisplay: "symbol",
      minimumFractionDigits: val % 1 === 0 ? 0 : 2
    });
    return formatter.format(val);
  }

  static getAbbreviatedPrice(value, ismobile) {
    let val = typeof value !== "number" ? Number(value) : value;
    const suffixes = ["", "K", "M"];
    let suffixNum = 0;

    if (val >= 1000000) {
      val = Math.trunc(val / 10);
      val /= 100000;
      suffixNum = 2;
    } else if (val >= 10000) {
      val = Math.trunc(val);
      val /= 1000;
      val = Math.round(val * 100) / 100;
      suffixNum = 1;
    }
    if (value >= 999000 && value <= 999999) {
      val = Math.round(val / 1000);
      suffixNum = 2;
    }
    if (ismobile) {
      val = Math.round(val);
    }
    const locale = Locale.getCurrent();
    const currency =
      {
        "en-US": "USD",
        "en-CA": "CAD",
        "fr-CA": "CAD",
        "es-US": "USD"
      }[locale] || "USD";
    const formatter = new Intl.NumberFormat(Locale.getCurrent(), {
      style: "currency",
      currency,
      currencyDisplay: "symbol",
      minimumFractionDigits: val % 1 === 0 ? 0 : 2
    });

    let formattedVal = formatter.format(val);
    const abbreviatedLetter = suffixes[suffixNum];

    if (locale === "fr-CA") {
      const indexBeforeDollarSign = formattedVal.length - 2;
      formattedVal = `${formattedVal.slice(
        0,
        indexBeforeDollarSign
      )}${abbreviatedLetter}${formattedVal.slice(indexBeforeDollarSign)}`;
    } else {
      formattedVal = `${formatter.format(val)}${abbreviatedLetter}`;
    }

    return formattedVal;
  }

  static getNumberFormat(value) {
    const val = typeof value !== "number" ? Number(value) : value;
    const formatter = new Intl.NumberFormat(Locale.getCurrent(), {});
    return formatter.format(val);
  }

  static sanitizedHtml(text) {
    return (
      <span
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(text, { USE_PROFILES: { html: true } })
        }}
      />
    );
  }

  // convert date value in yyyy-mm-ddThh:mm to formatted locale date
  // toLocaleDateString only convert US date to m-d-yyyy when date and month is single digit while we want mm-dd-yyyy
  static getDateFormat(dateString, locale = "en-US") {
    if (!dateString) {
      return null;
    }
    const date = new Date(dateString);
    if (date.toString() === "Invalid Date") {
      return null;
    }
    if (locale.includes("US")) {
      const year = date.getFullYear();

      let month = (1 + date.getMonth()).toString();
      month = month.length > 1 ? month : `0${month}`;

      let day = date.getDate().toString();
      day = day.length > 1 ? day : `0${day}`;

      return `${month}/${day}/${year}`;
    }

    return date.toLocaleDateString(locale);
  }

  static getLocaleFromURL(optionalUrl_ = "") {
    const optionalUrl = optionalUrl_ || window.location.search;
    const foundIdx = optionalUrl.indexOf("lang=");
    let locale = "";
    if (foundIdx > -1 && optionalUrl.length - foundIdx >= 10) {
      locale = optionalUrl.substr(foundIdx + 5, 5);
      locale = locale.replace("_", "-");
      if (!/([a-z][a-z]-[A-Z][A-Z])/.test(locale)) {
        // Validate it
        locale = "";
      }
    }

    return locale;
  }

  static getLocale() {
    // Note that navigator.languages[0] is used by Accept-Language (https://developer.mozilla.org/en-US/docs/Web/API/NavigatorLanguage/languages)
    // Our api uses standard hyphen; cc uses underscores
    const urlLocale = Locale.getLocaleFromURL();

    const curLocale =
      urlLocale ||
      (navigator.languages && navigator.languages[0]) ||
      navigator.language;

    // only return supported locales
    return (Locale.exists(curLocale) && curLocale) || "en-US";
  }
}
